import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { FinancialCashierShift } from '@gipi-financial/cashier-shift/models/cashier-shift.model';
import { FinancialCashierShiftService } from '@gipi-financial/cashier-shift/services/cashier-shift.service';
import { FinancialConfigurationService } from '@gipi-financial/configuration/services/configuration.service';
import { FinancialCreditClientType } from '@gipi-financial/credit-client/enums/credit-client-type.enum';
import { FinancialCreditClientCancelDTO } from '@gipi-financial/credit-client/models/dto/cancel-credit-client.dto';
import { FinancialCreditClientConsultDTO } from '@gipi-financial/credit-client/models/dto/credit-client-consult.dto';
import { FinancialCreditClientService } from '@gipi-financial/credit-client/services/credit-client.service';
import { FinancialPostingCategoryOperation } from '@gipi-financial/posting-category/enums/operation.enum';
import { FinancialPostingCategorySelectDTO } from '@gipi-financial/posting-category/models/dto/posting-category-select.dto';
import { FinancialPostingCategoryService } from '@gipi-financial/posting-category/services/posting-category.service';
import { OAuthUser } from '@gipi-financial/user/models/user.model';
import { APP_MESSAGES, AuthenticationService, CurrencyUtil, DateUtil, GIPIAbstractComponent, GIPIBaseService, GIPIPageModel, GIPISortModel, INJECTOR, ObjectUtil, StringUtil, TokenDTO } from '@gipisistemas/ng-core';
import { finalize } from 'rxjs/operators';

export interface CreditClientCancelDialogData {
    creditClient: FinancialCreditClientConsultDTO;
}

@Component({
    templateUrl: './credit-client-cancel-dialog.component.html',
    styles: [`
        :host {
            display: flex;
            flex-direction: column;
            height: 100%;
        }`]
})
export class CreditClientCancelDialogComponent extends GIPIAbstractComponent implements OnInit {

    private _cashierShift: FinancialCashierShift;
    private _creditTypeValue: FinancialCreditClientType = 'CREDIT';

    public description: string = '';
    public postingCategory: FinancialPostingCategorySelectDTO = null;

    postingCategoryFindByValueFn = async (value: string, page: number) => {
        /**
         * Aqui deve ser ao contrario.
         * Se estou cancelando um crédito na teoria estou fazendo um débito no saldo do cliente.
         * Se estou cancelando um débito na teoria estou fazendo um crédito no saldo do cliente.
         */
        const type: FinancialPostingCategoryOperation = this._creditTypeValue === 'DEBIT' ? 'CREDIT' : 'DEBIT';
        const result: GIPIPageModel<FinancialPostingCategorySelectDTO> = await this._postingCategoryService.findByValue(value, page, 10, new GIPISortModel('fullCode', 'ASC'), 'v1', type, true).toPromise();
        return result;
    };
    postingCategoryFn = (obj: FinancialPostingCategorySelectDTO) => `${obj.fullCode} - ${obj.description}`;

    constructor(
        protected baseService: GIPIBaseService,
        protected activatedRoute: ActivatedRoute,
        private _authenticationService: AuthenticationService<OAuthUser>,
        private _creditClientService: FinancialCreditClientService,
        private _cashierShiftService: FinancialCashierShiftService,
        private _postingCategoryService: FinancialPostingCategoryService,
        private _configurationService: FinancialConfigurationService,
        public dialogRef: MatDialogRef<CreditClientCancelDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CreditClientCancelDialogData = { creditClient: null },
    ) {
        super(baseService, activatedRoute);

        this.dialogRef.disableClose = true;
    }

    ngOnInit(): void {
        super.ngOnInit();
        this._creditTypeValue = this.data.creditClient.type;
    }

    protected async onInitForm(): Promise<void> {
        this._generateDescription();

        if (this._userIsCashier()) {
            this._getCashierShiftOpenedByUser(true).then(async cashierShift => {
                if (!ObjectUtil.isNull(cashierShift)) {
                    this._cashierShift = ObjectUtil.clone(cashierShift);
                } else {
                    this.close(false);
                }
            });
        } else {
            this._cashierShift = null;
        }

        this._configurationService.findEnabled()
            .pipe(finalize(() => (this.loading = false)))
            .subscribe(configuration => {
                let postingCategoryConfig: FinancialPostingCategorySelectDTO = null;
                if (!ObjectUtil.isNull(configuration)) {
                    /**
                     * Aqui deve ser ao contrario.
                     * Se estou cancelando um crédito na teoria estou fazendo um débito no saldo do cliente.
                     * Se estou cancelando um débito na teoria estou fazendo um crédito no saldo do cliente.
                     */
                    if (this._creditTypeValue === 'DEBIT') {
                        postingCategoryConfig = FinancialPostingCategorySelectDTO.transformToDTO(configuration.defaultClientCreditCategoryToCredit);
                    } else {
                        postingCategoryConfig = FinancialPostingCategorySelectDTO.transformToDTO(configuration.defaultClientCreditCategoryToDebit);
                    }
                }

                if (!ObjectUtil.isNull(postingCategoryConfig)) {
                    this.postingCategory = ObjectUtil.clone(postingCategoryConfig);
                }
            }, error => {
                this.loading = false;
                this.handleError(error);
            })
    }

    public confirm(): void {
        try {
            this.loading = true;
            if (StringUtil.isEmpty(this.description)) {
                this.addWarningMessage('Campo observação é obrigatório e não foi informado');
                return;
            }
            if (ObjectUtil.isNull(this.postingCategory)) {
                this.addWarningMessage('Campo categoria é obrigatório e não foi informado');
                return;
            }

            const creditClientCancel: FinancialCreditClientCancelDTO = new FinancialCreditClientCancelDTO();
            creditClientCancel.creditClientId = this.data.creditClient.id;
            creditClientCancel.cashierShiftId = !ObjectUtil.isNull(this._cashierShift) ? this._cashierShift.id : null;
            creditClientCancel.description = this.description;
            creditClientCancel.postingCategoryId = !ObjectUtil.isNull(this.postingCategory) ? this.postingCategory.id : null;

            this._creditClientService.cancelCredit(creditClientCancel).toPromise().then(creditClient => {
                if (!ObjectUtil.isNull(creditClient)) {
                    this.addSuccessMessage(INJECTOR.get(APP_MESSAGES).SUCCESS);
                    this.close(true);
                }
            }, error => {
                this.loading = false;
                this.handleError(error);
            });
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

    /**
     * @template RELOAD_TABLE Dá reload na grid atualizando os registros
     * @template REMARK_SELECTED Volta a tela anterior e seleciona os registros na grid
     * @template NONE Não acontece nada, só volta para tela anterior
     */
    public close(isSaved: boolean): void {
        this.dialogRef.close(isSaved);
    }

    private async _getCashierShiftOpenedByUser(closeModal: boolean): Promise<FinancialCashierShift> {
        return new Promise(async (resolve, reject) => {
            if (this._userIsCashier()) {
                await this._cashierShiftService.getOpenedByCurrentUser().toPromise().then(cashierShift => {
                    if (ObjectUtil.isNewModel(cashierShift)) {
                        resolve(null);
                        if (closeModal) {
                            this.close(false);
                        }
                        this.handleError(`Não é possível realizar o cancelamento. Operador de caixa deve abrir um turno.`);
                    } else {
                        resolve(cashierShift);
                    }
                }, (error) => {
                    resolve(null);
                    if (error.toLowerCase().includes('a consulta não retornou resultado único')) {
                        this.handleError(`Erro ao buscar turnos. ${error}`);
                    } else {
                        this.handleError(error);
                    }
                });
            }
        });
    }

    private _userIsCashier(): boolean {
        const token: TokenDTO<OAuthUser> = this._authenticationService.token;
        return (!ObjectUtil.isNull(token) && token.user.cashier);
    }

    private _generateDescription(): void {
        try {
            const { type, clientName, documentNumber, date, amount } = this.data.creditClient;

            const creditOrDébit: string = (type === 'CREDIT') ? 'crédito' : 'débito';
            const dateFormatted: string = DateUtil.format(date, DateUtil.DATE_FORMAT);
            const amountFormatted: string = CurrencyUtil.transform(amount || 0, '1.2-2');

            this.description = `Cancelamento do lançamento de ${creditOrDébit} referente ao documento nº ${documentNumber}, emitido em ${dateFormatted}, do cliente ${clientName}, no valor de ${amountFormatted}.`;
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

}
